import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { RotateCcw } from 'lucide-react';
import type { PipelineStep } from '@/api/alibaba';

interface PipelineTooltipProps {
  step: PipelineStep;
  allSteps: PipelineStep[];
  onRetry: (stepId: string) => void;
  children: React.ReactNode;
}

function formatDuration(ms: number | null): string {
  if (ms === null) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.round((ms % 60_000) / 1000);
  return `${m}m ${s}s`;
}

function formatTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export function PipelineTooltip({ step, allSteps, onRetry, children }: PipelineTooltipProps) {
  const dependSteps = allSteps.filter(s => step.depends_on.includes(s.step_id));
  const unlockSteps = allSteps.filter(s => step.unlocks.includes(s.step_id));

  // Explain why a pending step is waiting
  const pendingReason = step.status === 'pending'
    ? dependSteps.filter(d => d.status !== 'completed').map(d => `${d.step_id} (${d.status})`)
    : [];

  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-xs p-3 space-y-2">
        <div className="font-semibold text-sm">{step.step_id}: {step.step_name}</div>

        {/* Timing */}
        <div className="text-xs space-y-0.5 text-muted-foreground">
          <div>開始: {formatTime(step.started_at)}</div>
          <div>完成: {formatTime(step.completed_at)}</div>
          <div>耗時: {formatDuration(step.duration_ms)}</div>
        </div>

        {/* Dependencies */}
        {dependSteps.length > 0 && (
          <div className="text-xs">
            <span className="font-medium">前置: </span>
            {dependSteps.map(d => (
              <span
                key={d.step_id}
                className={`inline-block mr-1 px-1 rounded ${
                  d.status === 'completed' ? 'bg-green-100 text-green-700' :
                  d.status === 'failed' ? 'bg-red-100 text-red-700' :
                  'bg-gray-100 text-gray-600'
                }`}
              >
                {d.step_id}
              </span>
            ))}
          </div>
        )}

        {/* Unlocks */}
        {unlockSteps.length > 0 && (
          <div className="text-xs">
            <span className="font-medium">解鎖: </span>
            {unlockSteps.map(u => (
              <span key={u.step_id} className="inline-block mr-1 px-1 rounded bg-blue-100 text-blue-700">
                {u.step_id}
              </span>
            ))}
          </div>
        )}

        {/* Output summary */}
        {step.output_summary && (
          <div className="text-xs">
            <span className="font-medium">輸出: </span>{step.output_summary}
          </div>
        )}

        {/* Error + retry */}
        {step.status === 'failed' && step.error_message && (
          <div className="space-y-1">
            <div className="text-xs text-red-600 bg-red-50 rounded px-2 py-1">
              {step.error_message}
            </div>
            <Button size="sm" variant="outline" className="h-6 text-xs" onClick={() => onRetry(step.step_id)}>
              <RotateCcw className="h-3 w-3 mr-1" />重試
            </Button>
          </div>
        )}

        {/* Pending reason */}
        {step.status === 'pending' && pendingReason.length > 0 && (
          <div className="text-xs text-amber-600">
            等待中: {pendingReason.join(', ')}
          </div>
        )}
      </TooltipContent>
    </Tooltip>
  );
}
